import { useState } from 'react';
import { Plus, Trash2, Star } from 'lucide-react';
import {
  useAddInstructor,
  useRemoveInstructor,
  useUpdateInstructorLead,
  useInstructorCandidates,
} from '@/api/course-builder';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { Combobox } from '@/components/shared/combobox';
import type { CourseInstructor } from '@/types';

type InstructorSectionProps = {
  courseId: number;
  instructors: CourseInstructor[];
  isDraft: boolean;
};

export function InstructorSection({ courseId, instructors, isDraft }: InstructorSectionProps) {
  const { data: candidates } = useInstructorCandidates();
  const addMut = useAddInstructor();
  const removeMut = useRemoveInstructor();
  const leadMut = useUpdateInstructorLead();

  const [showForm, setShowForm] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [isLead, setIsLead] = useState(false);
  const [removing, setRemoving] = useState<CourseInstructor | null>(null);

  const allUsers = candidates ?? [];
  const assignedIds = new Set(instructors.map((ci) => ci.user.id));
  const availableUsers = allUsers.filter((u) => !assignedIds.has(u.id));

  const handleAdd = () => {
    if (!selectedUserId) return;
    addMut.mutate(
      {
        courseId,
        data: { user_id: Number(selectedUserId), is_lead: isLead },
      },
      {
        onSuccess: () => {
          setSelectedUserId('');
          setIsLead(false);
          setShowForm(false);
        },
      },
    );
  };

  const handleRemove = () => {
    if (!removing) return;
    removeMut.mutate(
      { courseId, userId: removing.user.id },
      { onSuccess: () => setRemoving(null) },
    );
  };

  return (
    <div className="mb-8">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">Instructores</h3>
        {isDraft && (
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80"
          >
            <Plus className="h-3.5 w-3.5" /> Agregar instructor
          </button>
        )}
      </div>

      {isDraft && showForm && (
        <div className="mb-4 rounded-lg border border-border bg-muted/20 p-4">
          <div className="space-y-3">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">Usuario</label>
              <Combobox
                value={selectedUserId}
                onChange={setSelectedUserId}
                options={availableUsers.map((u) => ({
                  value: String(u.id),
                  label: `${u.first_name} ${u.last_name}`,
                  sublabel: u.email,
                }))}
                placeholder="Seleccionar instructor..."
                clearable
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={isLead}
                onChange={(e) => setIsLead(e.target.checked)}
                className="rounded border-input"
              />
              Instructor principal (Lead)
            </label>
            <div className="flex items-center gap-2">
              <button
                onClick={handleAdd}
                disabled={addMut.isPending || !selectedUserId}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
              >
                {addMut.isPending ? 'Agregando...' : 'Agregar'}
              </button>
              <button
                onClick={() => {
                  setShowForm(false);
                  setSelectedUserId('');
                  setIsLead(false);
                }}
                className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
              >
                Cancelar
              </button>
            </div>
            {addMut.error && <p className="text-xs text-destructive">{addMut.error.message}</p>}
          </div>
        </div>
      )}

      {instructors.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-8">
          <p className="text-sm font-medium text-foreground">Sin instructores</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Agrega al menos un instructor principal para poder publicar
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Nombre
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Rol
                </th>
                {isDraft && (
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Acciones
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {instructors.map((ci) => (
                <tr key={ci.user.id} className="hover:bg-muted/30">
                  <td className="px-6 py-4 text-sm font-medium text-foreground">
                    {ci.user.first_name} {ci.user.last_name}
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{ci.user.email}</td>
                  <td className="px-6 py-4">
                    {ci.is_lead ? (
                      <span className="inline-flex items-center gap-1 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary ring-1 ring-primary/20">
                        <Star className="h-3 w-3" /> Principal
                      </span>
                    ) : isDraft ? (
                      <button
                        onClick={() =>
                          leadMut.mutate({ courseId, userId: ci.user.id, isLead: true })
                        }
                        disabled={leadMut.isPending}
                        className="text-xs text-muted-foreground underline decoration-dotted hover:text-primary"
                      >
                        Asignar como principal
                      </button>
                    ) : (
                      <span className="text-xs text-muted-foreground">Instructor</span>
                    )}
                  </td>
                  {isDraft && (
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => setRemoving(ci)}
                        className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                        aria-label="Eliminar instructor"
                        title="Eliminar instructor"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {removing && (
        <ConfirmDialog
          title="Eliminar instructor"
          message={`Eliminar a "${removing.user.first_name} ${removing.user.last_name}" como instructor de este curso?`}
          confirmLabel="Eliminar"
          variant="danger"
          isPending={removeMut.isPending}
          onConfirm={handleRemove}
          onCancel={() => setRemoving(null)}
        />
      )}
    </div>
  );
}
